import Cookies from 'js-cookie';
import { ctxGeo } from 'utils/brijContext';
import { CookieSettings } from 'types/ProductDetailsType';
import { CookiesConsentType } from 'types/CookiesConsentType';
import {
  ConsentModeObject,
  mapCookiesConsentToConsentMode,
} from 'utils/consentUtils';

export const COOKIE_CONSENT_CAPTURED_AT_COOKIE = 'cookieConsentCapturedAt';

export type CookieConsentSnapshot = ConsentModeObject & {
  source: 'banner' | 'geo-exempt-default' | 'default';
  capturedAt: string;
  allConsented: boolean;
};

// Single shared geo computation for the CookieBanner AND the event snapshot,
// so the two can never disagree on whether a banner was required.
// Phase 2 semantics: geo comes only from the Redis context (the legacy
// brij-*-code geo cookies are no longer set). FAIL CLOSED: if the banner is
// enabled for this brand and we cannot determine the visitor's location,
// require the banner rather than silently auto-granting consent
// (compliance-safe default).
// EXCEPTION - embedded contexts (brand-dashboard admin registration /
// preview iframes): those never have a redirect-owned Redis session, so
// unknown geo is EXPECTED there, and a fail-closed banner would block the
// admin's flow (and could write an implicit consent onto the customer's
// record). Inside an iframe, unknown geo keeps the pre-Phase-2 behavior;
// a geo MATCH still shows the banner as before. window.mode is set
// asynchronously via postMessage, so we gate on the synchronous iframe
// check instead of isPreviewMode().
export const isCookieBannerGeoRequired = (
  cookieSettings?: CookieSettings | null
): boolean => {
  const isEmbedded =
    typeof window !== 'undefined' && window.self !== window.top;
  const geoKnown = !!(
    ctxGeo('country_code') ||
    ctxGeo('continent_code') ||
    ctxGeo('region_code')
  );
  return Boolean(
    cookieSettings?.enabled &&
      ((!geoKnown && !isEmbedded) ||
        cookieSettings?.countries?.some(
          ({ value }) => value === ctxGeo('country_code')
        ) ||
        cookieSettings?.countries?.some(
          ({ value }) => value === ctxGeo('continent_code')
        ) ||
        cookieSettings?.usaStates?.some(
          ({ value }) => value === ctxGeo('region_code')
        ))
  );
};

const computeAllConsented = (consentMode: ConsentModeObject): boolean =>
  consentMode.ad_storage === 'granted' &&
  consentMode.ad_user_data === 'granted' &&
  consentMode.ad_personalization === 'granted' &&
  consentMode.analytics_storage === 'granted' &&
  consentMode.functionality_storage === 'granted' &&
  consentMode.personalization_storage === 'granted';

const buildSnapshot = (
  consentMode: ConsentModeObject,
  source: CookieConsentSnapshot['source'],
  capturedAt?: string
): CookieConsentSnapshot => ({
  ...consentMode,
  source,
  capturedAt: capturedAt || new Date().toISOString(),
  allConsented: computeAllConsented(consentMode),
});

// Resolution mirrors what the page's gtag consent state actually is:
// 1. banner decision persisted in the 'cookieConsent' cookie -> map it;
// 2. no decision + banner not required -> all granted (matches the
//    CookieBanner all-grant effect; under Phase 2 fail-closed semantics this
//    means geo is KNOWN and not covered, or an embedded iframe context);
// 3. no decision + banner required (including unknown geo outside iframes,
//    per fail-closed) -> Consent Mode defaults (all denied, security granted
//    - matches the gtag('consent','default') in index.html).
// Returns undefined in admin-registration/preview modes (the admin's own
// browser consent must never be stamped on a customer's events - the server
// fallback chain resolves the customer's stored consent instead) and on any
// unexpected error (never block event logging).
const getCookieConsent = (
  cookieSettings?: CookieSettings | null
): CookieConsentSnapshot | undefined => {
  try {
    const mode = (window as any).mode;
    if (mode === 'adminRegistration' || mode === 'preview') return undefined;

    const storedConsent = Cookies.get('cookieConsent');
    if (storedConsent) {
      // Shape-guard the parse: 'null'/primitives/arrays are not a banner
      // decision - fall through to the geo tiers instead of fabricating one.
      let parsedConsent: CookiesConsentType | null = null;
      try {
        const parsed = JSON.parse(storedConsent);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedConsent = parsed as CookiesConsentType;
        }
      } catch (parseError) {
        parsedConsent = null;
      }
      if (parsedConsent) {
        return buildSnapshot(
          mapCookiesConsentToConsentMode(parsedConsent),
          'banner',
          Cookies.get(COOKIE_CONSENT_CAPTURED_AT_COOKIE)
        );
      }
      // Present-but-invalid cookie: the CookieBanner all-grant effect is
      // suppressed by the cookie's mere presence, so gtag stays all-denied -
      // matching that (never overclaiming) means stamping 'default' here
      // rather than falling through to the geo-exempt all-grant tier.
      return buildSnapshot(
        mapCookiesConsentToConsentMode({
          essential: true,
          functional: false,
          analytics: false,
          marketing: false,
          social: false,
        }),
        'default'
      );
    }

    if (!isCookieBannerGeoRequired(cookieSettings)) {
      return buildSnapshot(
        mapCookiesConsentToConsentMode({
          essential: true,
          functional: true,
          analytics: true,
          marketing: true,
          social: true,
        }),
        'geo-exempt-default'
      );
    }

    return buildSnapshot(
      mapCookiesConsentToConsentMode({
        essential: true,
        functional: false,
        analytics: false,
        marketing: false,
        social: false,
      }),
      'default'
    );
  } catch (error) {
    console.error('Error resolving cookie consent snapshot:', error);
    return undefined;
  }
};

export default getCookieConsent;
